import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { UserPlus, Trash2, ShieldCheck, History, Save } from "lucide-react";
import { RoleLogDialog } from "@/components/RoleLogDialog";
import { CHURCH_ROLES, ROLE_LEVEL, roleName } from "@/lib/access";

const roleLabel = (s) => (s.is_owner ? "Owner" : s.church_role || (s.role === "admin" ? "Admin (legacy)" : s.role === "staff" ? "Staff (legacy)" : "Member"));

const EMPTY = { name: "", email: "", password: "", role: "Execom" };

export default function Team() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("All fields are required"); return; }
    try {
      await api.post("/staff", form);
      toast.success(`${form.name} added to the team`);
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add team member"); }
  };

  const [pending, setPending] = useState({});
  const [saving, setSaving] = useState(false);
  const pendingCount = Object.keys(pending).length;

  const pickRole = (s, role) => {
    const next = { ...pending };
    if (role === s.church_role) delete next[s.id]; else next[s.id] = role;
    setPending(next);
  };

  const saveChanges = async () => {
    setSaving(true);
    const failed = {};
    let ok = 0;
    for (const [id, role] of Object.entries(pending)) {
      const s = staff.find((x) => x.id === id);
      try { await api.patch(`/staff/${id}/role`, { role }); ok += 1; }
      catch (e) { failed[id] = role; toast.error(`${s?.name || s?.email}: ${e.response?.data?.detail || "Could not change role"}`); }
    }
    if (ok) toast.success(`${ok} role change${ok === 1 ? "" : "s"} saved`);
    setPending(failed); setSaving(false); load();
  };

  const rank = (s) => (s.is_owner ? -1 : CHURCH_ROLES.includes(s.church_role) ? CHURCH_ROLES.indexOf(s.church_role)
    : { owner: 0, full: 1, read_all: 2, read_nonfin: 3, member: 4 }[s.access] ?? 4);
  const sorted = [...staff].sort((a, b) => rank(a) - rank(b) || (a.name || a.email).localeCompare(b.name || b.email, undefined, { sensitivity: "base" }));

  const remove = async (id) => {
    try { await api.delete(`/staff/${id}`); toast.success("Team member removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-[#526050]">{staff.length} logins · pick new roles, then Save Changes</p>
        <div className="ml-auto flex flex-wrap gap-2">
          {pendingCount > 0 && (
            <Button variant="ghost" data-testid="discard-role-changes" onClick={() => setPending({})} className="h-11 text-[#526050]">Discard</Button>
          )}
          <Button data-testid="save-role-changes" onClick={saveChanges} disabled={!pendingCount || saving}
            className="h-11 bg-[#D97706] hover:bg-[#B45309] text-white disabled:opacity-50">
            <Save className="h-4 w-4 mr-2" />{saving ? "Saving..." : `Save Changes${pendingCount ? ` (${pendingCount})` : ""}`}
          </Button>
        <Button variant="outline" data-testid="role-log-button" onClick={() => setLogOpen(true)}
          className="h-11 border-[#2D5A27] text-[#2D5A27] hover:bg-[#EAF2E8]">
          <History className="h-4 w-4 mr-2" /> Activity Log
        </Button>
        <Button data-testid="add-staff-button" onClick={() => { setForm(EMPTY); setOpen(true); }}
          className="h-11 bg-[#2D5A27] hover:bg-[#23471F] text-white">
          <UserPlus className="h-4 w-4 mr-2" /> Add Team Member
        </Button>
        </div>
      </div>

      <Card className="bg-white border border-[#E5E0D8] shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-[#E5E0D8]">
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((s) => (
              <TableRow key={s.id} className={`border-[#E5E0D8] ${pending[s.id] ? "bg-[#FEF3C7]/60" : ""}`} data-testid={`staff-row-${s.id}`}>
                <TableCell className="font-medium text-[#1C241B]">
                  {s.name} {s.id === user?.id && <span className="text-xs text-[#7A8A78]">(you)</span>}
                </TableCell>
                <TableCell className="text-sm text-[#526050]">{s.email}</TableCell>
                <TableCell>
                  {s.id === user?.id || s.is_owner ? (
                    <Badge variant="outline" className="font-medium bg-[#EAF2E8] text-[#2D5A27] border-[#cfe3cb]" data-testid={`staff-role-badge-${s.id}`}>
                      <ShieldCheck className="h-3 w-3 mr-1" />{roleLabel(s)}
                    </Badge>
                  ) : (
                    <Select value={pending[s.id] || (CHURCH_ROLES.includes(s.church_role) ? s.church_role : undefined)} onValueChange={(v) => pickRole(s, v)}>
                      <SelectTrigger className="h-9 w-40" data-testid={`change-role-${s.id}`}><SelectValue placeholder={roleLabel(s)} /></SelectTrigger>
                      <SelectContent>
                        {CHURCH_ROLES.map((r) => (
                          <SelectItem key={r} value={r} disabled={r === "Member" && !s.member_id} data-testid={`role-option-${r.replace(" ", "-")}-${s.id}`}>
                            {roleName(r)}{r === "Member" && !s.member_id ? " (no member record)" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <p className="mt-1 text-[11px] text-[#7A8A78]">{pending[s.id] ? <span className="font-semibold text-[#B45309]" data-testid={`pending-role-${s.id}`}>Unsaved · {ROLE_LEVEL[pending[s.id]]}</span> : ROLE_LEVEL[s.church_role] || (s.is_owner ? "Full access · owner" : "Legacy access — pick a role")}</p>
                </TableCell>
                <TableCell className="text-right">
                  {s.id !== user?.id && !s.is_owner && (
                    <Button size="icon" variant="ghost" onClick={() => remove(s.id)} data-testid={`delete-staff-${s.id}`} className="h-8 w-8 text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="staff-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Full name</Label>
              <Input data-testid="staff-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" data-testid="staff-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Temporary password</Label>
              <Input data-testid="staff-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="They can reset it later" />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHURCH_ROLES.map((r) => (
                    <SelectItem key={r} value={r} disabled={r === "Member"} data-testid={`staff-role-option-${r.replace(" ", "-")}`}>
                      {roleName(r)}{r === "Member" ? " (needs a member record — use Members page)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={invite} data-testid="save-staff-button" className="bg-[#2D5A27] hover:bg-[#23471F] text-white">Add Member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <RoleLogDialog open={logOpen} onOpenChange={setLogOpen} />
    </div>
  );
}
